import React, { useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import SimpleComboBox from '@/components/SimpleComboBox'
import CategoryPicker, { useMappableCategories } from './CategoryPicker'
import { accountLabel } from './VaultMappingRow'
import {
  ALL_DATES,
  type ContactCategoryMapping,
  type Direction,
  type XeroAccount,
  type XeroContact,
  currentMapping,
  localToday,
  scheduledMapping,
  useRemoveContactOverride,
  useSaveCategoryMapping,
} from '@/services/api/integrations/useXeroMapping'
import { useAccountingProviderLabel } from '@/services/api/integrations/useAccountingProviderLabel'

const formatDate = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString()

/**
 * Category editor shared by account mappings and contact overrides. The first
 * mapping applies to every transaction; changing one asks from when.
 */
function MappingEditor({
  direction,
  hasExisting,
  initialCategoryId,
  saving,
  onSave,
  onCancel,
}: {
  direction: Direction
  hasExisting: boolean
  initialCategoryId: number | null
  saving: boolean
  onSave: (categoryId: number, effectiveFrom: string) => void
  onCancel: () => void
}) {
  const [categoryId, setCategoryId] = useState<number | null>(initialCategoryId)
  const [from, setFrom] = useState(localToday())

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-layout-secondary p-3 sm:flex-row sm:items-end">
      <div className="min-w-0 flex-1">
        <p className="mb-1 text-xs text-muted-foreground">Category</p>
        <CategoryPicker
          direction={direction}
          value={categoryId}
          onChange={setCategoryId}
        />
      </div>
      {hasExisting && (
        <label className="block sm:w-44">
          <span className="mb-1 block text-xs text-muted-foreground">
            Apply from
          </span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!categoryId || !from || saving}
          onClick={() =>
            categoryId && onSave(categoryId, hasExisting ? from : ALL_DATES)
          }
        >
          Save
        </Button>
      </div>
    </div>
  )
}

function MappingSummary({
  current,
  scheduled,
  categoryName,
}: {
  current?: {
    pf_category_id: number
    effective_from: string
    category?: { name: string }
  }
  scheduled?: {
    pf_category_id: number
    effective_from: string
    category?: { name: string }
  }
  categoryName: (id: number) => string
}) {
  return (
    <span className="text-sm">
      {current ? (
        <>
          {current.category?.name ?? categoryName(current.pf_category_id)}
          {current.effective_from !== ALL_DATES && (
            <span className="text-xs text-muted-foreground">
              {' '}
              since {formatDate(current.effective_from)}
            </span>
          )}
        </>
      ) : (
        <span className="flex items-center gap-1 text-amber-700">
          <AlertTriangle className="size-4" /> Not mapped — goes to Unreconciled
        </span>
      )}
      {scheduled && (
        <span className="block text-xs text-muted-foreground">
          → {scheduled.category?.name ?? categoryName(scheduled.pf_category_id)}{' '}
          from {formatDate(scheduled.effective_from)}
        </span>
      )}
    </span>
  )
}

/** One imported account's category for a direction, plus per-contact overrides. */
export default function AccountCategoryRow({
  account,
  direction,
  contacts,
  canEdit,
  rawTransactionId,
  onSaved,
}: {
  account: XeroAccount
  direction: Direction
  contacts: XeroContact[]
  canEdit: boolean
  rawTransactionId?: number
  onSaved?: () => void
}) {
  const save = useSaveCategoryMapping()
  const removeOverride = useRemoveContactOverride()
  const provider = useAccountingProviderLabel()
  const categories = useMappableCategories(direction)
  const categoryName = (id: number) =>
    categories.find((c) => c.id === id)?.name ?? `Category #${id}`
  const [editing, setEditing] = useState(false)
  const [showOverrides, setShowOverrides] = useState(false)
  const [editingContactId, setEditingContactId] = useState<number | null>(null)
  const [newContact, setNewContact] = useState<XeroContact | null>(null)

  const current = currentMapping(account.category_mappings, direction)
  const scheduled = scheduledMapping(account.category_mappings, direction)

  // Contact overrides for this direction, one entry per contact.
  const overrides = useMemo(() => {
    const byContact = new Map<number, ContactCategoryMapping[]>()
    for (const m of account.contact_category_mappings) {
      if (m.direction !== direction) continue
      byContact.set(m.external_contact_id, [
        ...(byContact.get(m.external_contact_id) ?? []),
        m,
      ])
    }
    return [...byContact.entries()].map(([contactId, rows]) => ({
      contactId,
      contact: contacts.find((c) => c.id === contactId),
      rows,
      current: currentMapping(rows, direction),
      scheduled: scheduledMapping(rows, direction),
    }))
  }, [account.contact_category_mappings, contacts, direction])

  const saveMapping = async (
    categoryId: number,
    effectiveFrom: string,
    contactId?: number,
  ) => {
    await save.mutateAsync({
      accountId: account.id,
      contactId,
      direction,
      categoryId,
      effectiveFrom,
      rawTransactionId,
    })
    setEditing(false)
    setEditingContactId(null)
    setNewContact(null)
    onSaved?.()
  }

  const contactName = (o: (typeof overrides)[number]) =>
    o.contact?.name ?? `Contact #${o.contactId}`

  return (
    <div className="space-y-2 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">{accountLabel(account)}</p>
          <div className="flex flex-wrap gap-1.5">
            {!account.is_active && (
              <Badge variant="outline">Archived in {provider}</Badge>
            )}
            {!account.has_transactions && (
              <span className="text-xs text-muted-foreground">
                No transactions yet
              </span>
            )}
          </div>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            <MappingSummary
              current={current}
              scheduled={scheduled}
              categoryName={categoryName}
            />
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
              >
                {current ? 'Change' : 'Map'}
              </Button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <MappingEditor
          direction={direction}
          hasExisting={!!current || !!scheduled}
          initialCategoryId={current?.pf_category_id ?? null}
          saving={save.isPending}
          onSave={(categoryId, from) => saveMapping(categoryId, from)}
          onCancel={() => setEditing(false)}
        />
      )}

      <button
        type="button"
        onClick={() => setShowOverrides(!showOverrides)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-txt-primary"
      >
        {showOverrides ? (
          <ChevronDown className="size-3.5" />
        ) : (
          <ChevronRight className="size-3.5" />
        )}
        Contact overrides ({overrides.length})
      </button>

      {showOverrides && (
        <div className="space-y-2 pl-4">
          <p className="text-xs text-muted-foreground">
            A contact override wins over the account mapping, e.g. "Sales" from
            Acme Corp goes to a client-specific category.
          </p>
          {overrides.map((o) =>
            editingContactId === o.contactId ? (
              <div key={o.contactId}>
                <p className="mb-1 text-sm">{contactName(o)}</p>
                <MappingEditor
                  direction={direction}
                  hasExisting
                  initialCategoryId={o.current?.pf_category_id ?? null}
                  saving={save.isPending}
                  onSave={(categoryId, from) =>
                    saveMapping(categoryId, from, o.contactId)
                  }
                  onCancel={() => setEditingContactId(null)}
                />
              </div>
            ) : (
              <div
                key={o.contactId}
                className="flex flex-wrap items-center justify-between gap-2"
              >
                <span className="text-sm">{contactName(o)}</span>
                <div className="flex items-center gap-1">
                  <MappingSummary
                    current={o.current}
                    scheduled={o.scheduled}
                    categoryName={categoryName}
                  />
                  {canEdit && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingContactId(o.contactId)}
                      >
                        Change
                      </Button>
                      <Button
                        variant="destructiveGhost"
                        size="sm"
                        iconOnly
                        aria-label={`Remove override for ${contactName(o)}`}
                        disabled={removeOverride.isPending}
                        onClick={() =>
                          removeOverride.mutate({
                            accountId: account.id,
                            contactId: o.contactId,
                            direction,
                          })
                        }
                      >
                        <X className="size-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ),
          )}

          {canEdit &&
            (newContact ? (
              <div>
                <p className="mb-1 text-sm">
                  Remember this for {newContact.name}
                </p>
                <MappingEditor
                  direction={direction}
                  hasExisting={false}
                  initialCategoryId={null}
                  saving={save.isPending}
                  onSave={(categoryId, from) =>
                    saveMapping(categoryId, from, newContact.id)
                  }
                  onCancel={() => setNewContact(null)}
                />
              </div>
            ) : (
              <div className="max-w-sm">
                <SimpleComboBox
                  items={contacts.filter(
                    (c) => !overrides.some((o) => o.contactId === c.id),
                  )}
                  selected={null}
                  setSelected={(contact: XeroContact | null) =>
                    setNewContact(contact)
                  }
                  getValue={(c: XeroContact) => `${c.name} #${c.id}`}
                  getOptionLabel={(c: XeroContact) => c.name}
                  getSelectedLabel={(c: XeroContact) => c.name}
                  placeholder="Add an override for a contact…"
                  empty="No contacts"
                />
              </div>
            ))}
        </div>
      )}
    </div>
  )
}
